import { store } from "@/lib/store.ts";
import { ChatRepository } from "@/repositories/chat.repository.ts";
import { uuid } from "@/interfaces";
import { ChatRoom, ChatRoomMessage, Contact } from "@/models/chat.model.ts";
import type { ChatFile, ChatMessageRequest } from "@shyne/shared/api";
import { touchRoom, upsertRoom, setRoomsWithUnreadUpdate } from "@/app/user/messages/messages.slice.ts";
import { upsertUsers } from "@/lib/store/users.slice.ts";
import { peopleOfMembers, peopleOfRooms, toChatRoom, toContact } from "@/repositories/chat-records.ts";
import { loadImage, mapMimeToFileType } from "@/lib/utils.ts";
import { ChatMessagesRepository } from "@/repositories/chat-messages.repository.ts";
import { addNew, removeTemp, replaceTemp } from "@/app/user/messages/chat-rooms.slice.ts";
import { resetUnread } from "@/lib/store/realtime.slice.ts";

function buildTemp(roomId: uuid, me: uuid, partial: Partial<ChatRoomMessage>): ChatRoomMessage {
  return {
    id: `tmp-${crypto.randomUUID()}`,
    room: roomId,
    sender: me,
    message: partial.message ?? "",
    is_file: partial.is_file ?? false,
    file_type: partial.file_type ?? null,
    is_system: false,
    meta_data: partial.meta_data as any,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
}

export interface SendMessageResult {
  textSent: boolean;
  failedText: string | null;
  sentFiles: number;
  failedFiles: File[];
  failure: unknown;
}

async function describedFile(url: string, file: File): Promise<ChatFile> {
  const mime = file.type || "application/octet-stream";
  const described: ChatFile = { url, name: file.name, mime, size: file.size };
  if (!mime.startsWith("image")) return described;
  try {
    const img = await loadImage(url);
    return { ...described, width: img.width, height: img.height };
  } catch (e) {
    console.error(e);
    return described;
  }
}

function showSent(roomId: uuid, real: ChatRoomMessage) {
  store.dispatch(addNew({ roomId, message: real }));
  store.dispatch(touchRoom({ roomId, last: real }));
}

export const ChatService = {
  async findOrCreateDM(otherUser: uuid): Promise<ChatRoom> {
    if (!store.getState().auth.user) throw new Error("Not authenticated");

    return toChatRoom(await ChatRepository.startDirect(otherUser, store.getState().organization.activeOrgId));
  },

  async resetUnread(roomId: uuid) {
    if (!store.getState().auth.user) throw new Error("Not authenticated");

    await ChatRepository.markRead(roomId);
  },

  async markRoomRead(roomId: uuid) {
    await ChatService.resetUnread(roomId);

    store.dispatch(resetUnread(roomId));
    const r = store.getState().messages.list.find((r) => r.id === roomId);
    if (r) store.dispatch(upsertRoom({ ...r, unread_count: 0 }));
  },

  async listChats() {
    const user = store.getState().auth.user;

    if (!user) throw new Error("Not authenticated");

    const rooms = await ChatRepository.listRooms();
    store.dispatch(upsertUsers(peopleOfRooms(rooms)));
    store.dispatch(setRoomsWithUnreadUpdate(rooms.map(toChatRoom)));
  },

  async listContacts(): Promise<Contact[]> {
    const activeOrgId = store.getState().organization.activeOrgId;

    if (!store.getState().auth.user) throw new Error("Not authenticated");
    if (!activeOrgId) return [];

    return (await ChatRepository.contacts(activeOrgId)).map(toContact);
  },

  async ensureSendersCached(roomId: uuid, senderIds: uuid[]) {
    const cached = store.getState().users;
    if (senderIds.every((id) => cached[id])) return;

    store.dispatch(upsertUsers(peopleOfMembers(await ChatRepository.members(roomId))));
  },

  async sendMessage(roomId: uuid, text: string, files: File[]): Promise<SendMessageResult> {
    const me = store.getState().auth.user;
    const dispatch = store.dispatch;

    if (!me) throw new Error("Not authenticated");

    async function sendSingle({ text, file }: { text?: string; file?: File }) {
      let temp: ChatRoomMessage | null = null;

      try {
        const request: ChatMessageRequest = file
          ? { kind: "file", file: await describedFile(await ChatMessagesRepository.uploadFile(roomId, file), file) }
          : { kind: "text", text: text ?? "" };

        temp = buildTemp(roomId, me.id, request.kind === "file"
          ? { message: request.file.name, is_file: true, file_type: mapMimeToFileType(request.file.mime), meta_data: { file: request.file } }
          : { message: request.text, is_file: false, file_type: null, meta_data: null });

        dispatch(addNew({ roomId, message: temp }));

        const real = await ChatMessagesRepository.send(roomId, request);

        dispatch(replaceTemp({ roomId, tempId: temp.id, real }));
        dispatch(touchRoom({ roomId, last: real }));
      } catch (e) {
        if (temp) dispatch(removeTemp({ roomId, tempId: temp.id }));
        console.error("Failed to send chat message:", e);
        throw e;
      }
    }

    const trimmedText = text.trim();
    const result: SendMessageResult = {
      textSent: false,
      failedText: null,
      sentFiles: 0,
      failedFiles: [],
      failure: null,
    };

    if (trimmedText) {
      try {
        await sendSingle({ text: trimmedText });
        result.textSent = true;
      } catch (error) {
        result.failedText = text;
        result.failure ??= error;
      }
    }

    for (const f of files) {
      try {
        await sendSingle({ file: f });
        result.sentFiles += 1;
      } catch (error) {
        result.failedFiles.push(f);
        result.failure ??= error;
      }
    }

    return result;
  },

  async post(roomId: uuid, request: ChatMessageRequest): Promise<ChatRoomMessage> {
    if (!store.getState().auth.user) throw new Error("Not authenticated");

    const real = await ChatMessagesRepository.send(roomId, request);
    showSent(roomId, real);
    return real;
  },

  showUpdatedMessage(roomId: uuid, updatedMessage: ChatRoomMessage): void {
    store.dispatch(replaceTemp({ roomId, tempId: updatedMessage.id, real: updatedMessage }));
  },
};
